import { useEffect, useState } from "react";
import { AlertTriangle } from "lucide-react";
import { api, money } from "../lib/api";
import { PageHeader, Panel } from "../components/common";

const FIELDS = [
  ["annual_sales", "Annual sales", "money"], ["avg_ticket", "Avg ticket", "money"], ["chargeback_rate", "Chargeback rate", "pct"],
  ["chargeback_fee", "Chargeback fee", "money"], ["hours_per_dispute", "Manual hours / dispute", "num"], ["hourly_rate", "Hourly rate", "money"],
  ["prevent_pct", "Prevented at checkout", "pct"], ["intercept_pct", "Intercepted pre-filing", "pct"], ["alert_fee", "Intercept alert fee", "money"],
  ["base_win_rate", "Baseline win rate", "pct"], ["ai_win_rate", "AI win rate", "pct"], ["appeal_pct", "Losses appealed", "pct"],
  ["appeal_success", "Appeal success", "pct"], ["merchants", "Licensed merchants", "num"], ["avg_mrr", "Avg MRR / merchant", "money"],
  ["rev_share_pct", "Revenue share", "pct"],
];
const DEFAULTS = { annual_sales: 500000, avg_ticket: 500, chargeback_rate: 0.02, chargeback_fee: 50, hours_per_dispute: 6, hourly_rate: 50, auto_hours: 0.1,
  prevent_pct: 0.2, intercept_pct: 0.15, alert_fee: 25, base_win_rate: 0.35, ai_win_rate: 0.65, appeal_pct: 0.15, appeal_success: 0.35,
  merchants: 50, avg_mrr: 1500, rev_share_pct: 0.12 };
const PRESETS = { "$500k · $500 ticket": { annual_sales: 500000, avg_ticket: 500 }, "$5M · $2.5k ticket": { annual_sales: 5000000, avg_ticket: 2500 },
  "$500M · $10k high-ticket": { annual_sales: 500000000, avg_ticket: 10000 } };
const inp = "w-full bg-canvas border border-white/15 px-2 py-1.5 font-mono text-xs focus:outline-none focus:border-brand";

const Field = ({ k, label, kind, v, set }) => (
  <label className="block">
    <span className="label-xs">{label}</span>
    <div className="relative mt-1">
      <input type="number" step="any" className={inp} data-testid={`roi-${k}`}
        value={kind === "pct" ? Math.round(v * 1000) / 10 : v}
        onChange={(e) => set(k, kind === "pct" ? Number(e.target.value) / 100 : Number(e.target.value))} />
      <span className="absolute right-2 top-1.5 font-mono text-[10px] text-white/30">{kind === "pct" ? "%" : kind === "money" ? "$" : ""}</span>
    </div>
  </label>
);

const Big = ({ k, v, c, testid }) => (
  <div className="p-5 border-r border-b border-line" data-testid={testid}>
    <p className="label-xs">{k}</p><p className="font-mono text-2xl font-extrabold mt-2 tabular" style={{ color: c }}>{v}</p>
  </div>
);

const Live = ({ l }) => (
  <Panel title="Measured on this platform · real data" testid="roi-live">
    <p className="font-mono text-4xl font-extrabold text-brand tabular" data-testid="roi-live-total">{money(l.measured_total)}</p>
    <p className="text-xs text-white/45 mt-1">value defended so far</p>
    <dl className="grid grid-cols-2 gap-3 mt-5 font-mono text-[11px]">
      {[["Recovered (won disputes)", money(l.recovered)], ["Win rate", `${l.win_rate}% (${l.won}W / ${l.lost}L)`],
        ["Evidence labor saved", `${money(l.labor_saved)} · ${l.packages_sealed} packages`], ["Auto-sealed at intake", l.auto_sealed],
        ["Exposure blocked pre-pay", `${money(l.exposure_blocked)} · ${l.blocked_checks} blocks`], ["Stepped-up checks", l.stepped_up_checks],
        ["Litigation-grade consents", l.consents_v2], ["Intercepts", `${l.intercepts} — ${l.intercept_note}`]].map(([k, v]) => (
        <div key={k}><dt className="label-xs">{k}</dt><dd className="mt-1 text-white/85">{v}</dd></div>
      ))}
    </dl>
  </Panel>
);

export default function Savings() {
  const [f, setF] = useState(DEFAULTS);
  const [m, setM] = useState(null);
  const [live, setLive] = useState(null);
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));
  useEffect(() => { api.get("/roi/live").then((r) => setLive(r.data)); }, []);
  useEffect(() => { const t = setTimeout(() => api.post("/roi/model", f).then((r) => setM(r.data)), 250); return () => clearTimeout(t); }, [f]);

  return (
    <div data-testid="savings-page">
      <PageHeader eyebrow="// chargeback economics" title="Savings Engine">
        {Object.entries(PRESETS).map(([l, p]) => (
          <button key={l} onClick={() => setF({ ...f, ...p })} className="font-mono text-[10px] tracking-wider px-3 py-2 border border-white/15 text-white/60 hover:border-brand hover:text-brand transition-colors" data-testid={`roi-preset-${l.split(" ")[0]}`}>{l}</button>
        ))}
      </PageHeader>
      {m && (
        <div className="grid grid-cols-2 lg:grid-cols-5 border-l border-t border-line bg-surface" data-testid="roi-kpis">
          <Big k="Chargebacks / yr" v={m.chargebacks.toLocaleString()} c="#FFB800" testid="roi-chargebacks" />
          <Big k="Loss per chargeback" v={money(m.loss_per_chargeback)} c="#F87171" testid="roi-loss-per" />
          <Big k="Baseline annual loss" v={money(m.baseline_annual_loss)} c="#F87171" testid="roi-baseline" />
          <Big k="With AI defense" v={money(m.ai_annual_loss)} c="#37E5D7" testid="roi-ai-loss" />
          <Big k={`Saved / yr · ${m.loss_reduction_pct}%`} v={money(m.annual_savings)} c="var(--brand)" testid="roi-savings" />
        </div>
      )}
      {m?.warnings.length > 0 && (
        <div className="mt-4 p-3 border border-[#FFB800]/40 text-[#FFB800] text-xs flex gap-2" data-testid="roi-warnings"><AlertTriangle className="w-4 h-4 shrink-0" />{m.warnings.join(" ")}</div>
      )}
      <div className="grid grid-cols-12 gap-6 mt-6">
        <Panel title="Assumptions" className="col-span-12 xl:col-span-3 self-start">
          <div className="grid grid-cols-2 xl:grid-cols-1 gap-3">{FIELDS.map(([k, l, kind]) => <Field key={k} k={k} label={l} kind={kind} v={f[k]} set={set} />)}</div>
        </Panel>
        <div className="col-span-12 xl:col-span-9 space-y-6">
          {m && (
            <Panel title="Savings by mechanism" testid="roi-table-panel">
              <div className="overflow-x-auto">
                <table className="w-full text-left min-w-[640px]" data-testid="roi-table">
                  <thead><tr className="label-xs">{["Mechanism", "Chargebacks affected", "Saved per", "Total / yr", "How it's counted"].map((h) => <th key={h} className="pb-3 font-normal pr-3">{h}</th>)}</tr></thead>
                  <tbody>
                    {m.rows.map((r) => (
                      <tr key={r.mechanism} className="border-b border-divider font-mono text-xs">
                        <td className="py-3 pr-3 text-white">{r.mechanism}</td><td className="pr-3">{r.affected}</td>
                        <td className="pr-3">{money(r.per_unit)}</td><td className="pr-3 text-brand font-bold">{money(r.total)}</td>
                        <td className="text-white/45 text-[11px] font-sans">{r.note}</td>
                      </tr>
                    ))}
                    <tr className="font-mono text-sm"><td className="pt-3 font-bold">Total</td><td /><td /><td className="pt-3 text-brand font-extrabold" data-testid="roi-total">{money(m.annual_savings)}</td><td className="pt-3 text-white/45 text-[11px]">{money(m.monthly_savings)} / month</td></tr>
                  </tbody>
                </table>
              </div>
            </Panel>
          )}
          {m && (
            <div className="grid md:grid-cols-3 gap-6">
              <Panel title="Card network standing" testid="roi-network">
                <p className="font-mono text-xs">Dispute ratio <span className={m.network.flagged_before ? "text-[#F87171]" : "text-white"}>{m.network.ratio_before}%</span> → <span className={m.network.flagged_after ? "text-[#F87171]" : "text-brand"}>{m.network.ratio_after}%</span></p>
                <p className="text-xs text-white/45 mt-2">Visa monitoring starts near {m.network.visa_threshold}%, Mastercard {m.network.mc_threshold}%. {m.network.flagged_before && !m.network.flagged_after ? "AI defense moves you out of the excessive-dispute zone." : m.network.flagged_after ? "Still above the Visa threshold — raise prevention." : "Below monitoring thresholds."}</p>
              </Panel>
              <Panel title="What it means for people" testid="roi-people">
                <p className="font-mono text-xs">"Chargeback tax" on prices <span className="text-[#F87171]">{m.people.chargeback_tax_before_pct}%</span> → <span className="text-brand">{m.people.chargeback_tax_after_pct}%</span></p>
                <p className="text-xs text-white/45 mt-2">Prices could drop ~{m.people.price_drop_possible_pct}% for honest customers, and high-ticket offers and financing become safe to sell.</p>
              </Panel>
              <Panel title="Platform business model" testid="roi-business">
                <p className="font-mono text-xs">SaaS ARR <span className="text-brand">{money(m.business.saas_arr)}</span></p>
                <p className="font-mono text-xs mt-1">Rev share / merchant {money(m.business.rev_share_per_merchant)}</p>
                <p className="font-mono text-xs mt-1">Rev share · portfolio {money(m.business.rev_share_portfolio)}</p>
                <p className="text-xs text-white/45 mt-2">{f.merchants} merchants × {money(f.avg_mrr)}/mo + {Math.round(f.rev_share_pct * 100)}% of savings delivered.</p>
              </Panel>
            </div>
          )}
          {live && <Live l={live} />}
        </div>
      </div>
    </div>
  );
}
